import React, {
  ClipboardEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
} from "react";
import styles from "./OtpInput.module.scss";

interface OtpInputProps {
  length?: number;
  value: string[];
  onChange: (value: string[]) => void;
  hasError?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
}

const sanitizeSingleDigit = (value: string) =>
  value.replace(/[^0-9]/g, "").slice(-1);

const sanitizePastedDigits = (value: string, length: number) =>
  value.replace(/[^0-9]/g, "").slice(0, length);

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  length = 4,
  hasError = false,
  disabled = false,
  autoFocus = false,
}) => {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const otpArray = useMemo(() => {
    const normalized = Array.from({ length }, (_, index) => value[index] ?? "");
    return normalized.map((item) => sanitizeSingleDigit(item));
  }, [length, value]);

  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  const handleValueChange = (nextValue: string[]) => {
    onChange(nextValue.map((item) => sanitizeSingleDigit(item)));
  };

  const updateAtIndex = (index: number, digit: string) => {
    const nextOtp = [...otpArray];
    nextOtp[index] = digit;
    handleValueChange(nextOtp);
  };

  const handleChange = (index: number, nextValue: string) => {
    const digit = sanitizeSingleDigit(nextValue);
    updateAtIndex(index, digit);

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (event.key === "Backspace") {
      if (otpArray[index]) {
        updateAtIndex(index, "");
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
        updateAtIndex(index - 1, "");
      }
      return;
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = sanitizePastedDigits(
      event.clipboardData.getData("text"),
      length,
    );

    if (!pasted) return;

    const nextOtp = Array.from({ length }, (_, index) => pasted[index] ?? "");
    handleValueChange(nextOtp);

    const focusIndex = Math.min(pasted.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputs}>
        {Array.from({ length }).map((_, index) => {
          const currentValue = otpArray[index] || "";

          return (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={1}
              value={currentValue}
              disabled={disabled}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onPaste={handlePaste}
              onFocus={(event) => event.target.select()}
              aria-label={`OTP digit ${index + 1}`}
              className={[
                styles.input,
                currentValue ? styles.filled : "",
                hasError ? styles.error : "",
              ]
                .filter(Boolean)
                .join(" ")}
            />
          );
        })}
      </div>
    </div>
  );
};

export default OtpInput;
